import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/shared/ui/atoms';
import { FlowDiagram } from '@/features/onboarding/components/FlowDiagram';
import { Prose } from '@/features/onboarding/components/Prose';
import { DEV_REQUEST_FLOW, FLOWS, HARNESS_TOOLS, type FlowStep } from '@/features/onboarding/data/harness';

function StepRow({ step, index }: { step: FlowStep; index: number }): React.JSX.Element {
  return (
    <li className="flex gap-3 border-b border-line-soft py-2.5 last:border-b-0">
      <span className="w-5 shrink-0 text-right text-[11px] font-bold text-faint">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <strong className="text-[13px]">{step.title}</strong>
          {step.skill && (
            <code className="rounded bg-accent-soft px-1.5 py-0.5 font-mono text-[11px] text-accent">{step.skill}</code>
          )}
        </div>
        <p className="mt-0.5 text-[12.5px] text-muted"><Prose text={step.detail} /></p>
        {step.output && (
          <p className="mt-0.5 text-[11.5px] text-faint">
            <span className="font-semibold">📎 남는 것</span> <Prose text={step.output} />
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * 하네스 워크플로 — 위는 개발요청 한 건이 배포까지 가는 길(흐름도), 아래는 그 길에서 부르는 스킬 순서.
 * 흐름별 표를 탭으로 가른 이유: 한 번에 하나의 흐름만 타므로, 넷을 늘어놓으면 지금 필요한 순서가 묻힌다.
 */
export function HarnessView(): React.JSX.Element {
  const [flowKey, setFlowKey] = useState(FLOWS[0]?.key ?? '');
  const flow = FLOWS.find((candidate) => candidate.key === flowKey) ?? FLOWS[0];

  return (
    <div className="flex flex-col gap-4">
      <Card title="🛤️ 개발요청부터 배포까지" hint="사람이 하는 일은 등록·담당자 지정·개발·QA 요청·머지뿐입니다 — 나머지는 콘솔과 봇이 합니다">
        <FlowDiagram
          steps={DEV_REQUEST_FLOW}
          loopBack={DEV_REQUEST_FLOW.findIndex((step) => step.title.includes('하네스로 개발'))}
          loopLabel="QA에서 이슈가 나오면 하네스로 개발로 돌아가 고칩니다"
        />
        <div className="mt-3 flex flex-wrap gap-3 text-[11.5px]">
          <Link to="/project-board/dev-requests" className="text-accent hover:underline">개발요청 열기 →</Link>
          <Link to="/project-board/qa" className="text-accent hover:underline">QA 열기 →</Link>
        </div>
      </Card>

      {flow && (
        <Card title="🧩 스킬 순서" hint="Claude Code에서 /스킬이름으로 부릅니다 — 다음에 뭘 부를지 모르겠다면 nd-harness-status를 불러 주세요">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {FLOWS.map((candidate) => (
              <button
                key={candidate.key}
                type="button"
                onClick={() => setFlowKey(candidate.key)}
                className={`rounded-md border px-2.5 py-1 text-[12px] ${
                  candidate.key === flow.key ? 'border-accent-line bg-accent-soft font-semibold text-accent' : 'border-line text-muted hover:bg-line-soft'
                }`}
              >
                {candidate.title}
              </button>
            ))}
          </div>
          <p className="mb-1 text-[12px] text-muted">{flow.when}</p>
          <ol className="flex flex-col">
            {flow.steps.map((step, index) => (
              <StepRow key={step.title} step={step} index={index} />
            ))}
          </ol>
        </Card>
      )}

      <Card title="🆘 막혔을 때 부르는 것" hint="흐름 밖에서 쓰는 도구입니다">
        <ul className="flex flex-col gap-1.5 text-[12.5px] text-muted">
          {HARNESS_TOOLS.map((tool) => (
            <li key={tool.skill}>
              <code className="mr-2 rounded bg-line-soft px-1 font-mono text-[11.5px] text-ink">{tool.skill}</code>
              {tool.when}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
